import React, { useMemo, useState } from 'react';
import { Button, Form, Input } from 'antd';
import type { FormInstance } from 'antd';
import { SelectableOption } from '../SelectableOption';
import { StepHeader } from '../StepHeader';
import type { HtsQuestionFormValues } from '../htsQuestionFormTypes';
import styles from '../HtsQuestionForm.module.scss';

interface SectionChipsProps {
  value?: string;
  onChange?: (value: string) => void;
  options: string[];
}

const SectionChips: React.FC<SectionChipsProps> = ({ value, onChange, options }) => (
  <div className={styles.chipRow} role="radiogroup" aria-label="Section">
    {options.map((name) => (
      <SelectableOption key={name} variant="chip" selected={value === name} onClick={() => onChange?.(name)}>
        {name}
      </SelectableOption>
    ))}
  </div>
);

interface Step2SectionProps {
  form: FormInstance<HtsQuestionFormValues>;
  sectionOptions: string[];
}

export const Step2Section: React.FC<Step2SectionProps> = ({ form, sectionOptions }) => {
  const [newSectionValue, setNewSectionValue] = useState('');
  const currentSection: string | undefined = Form.useWatch('section', { form, preserve: true });

  const chipOptions = useMemo(() => {
    const trimmed = currentSection?.trim();
    if (trimmed && !sectionOptions.includes(trimmed)) return [...sectionOptions, trimmed];
    return sectionOptions;
  }, [sectionOptions, currentSection]);

  const addNewSection = () => {
    const trimmed = newSectionValue.trim();
    if (!trimmed) return;
    form.setFieldValue('section', trimmed);
    setNewSectionValue('');
  };

  return (
    <div>
      <StepHeader
        stepNumber={2}
        title="Where does it live?"
        description="Groups this question under a labeled heading candidates see. Pick an existing section or add a new one."
      />

      <Form.Item name="section" rules={[{ required: true, message: 'Section is required' }]}>
        <SectionChips options={chipOptions} />
      </Form.Item>

      <div className={styles.addSectionRow}>
        <Input
          placeholder="Type a new section name"
          value={newSectionValue}
          onChange={(e) => setNewSectionValue(e.target.value)}
          onPressEnter={addNewSection}
        />
        <Button onClick={addNewSection}>+ Add</Button>
      </div>

      <Form.Item
        name="helpText"
        label="Help Text"
        extra="Optional. Shown to the candidate under the question, for extra clarification."
      >
        <Input placeholder="e.g. Include approximate dates if you're unsure of exact ones." />
      </Form.Item>
    </div>
  );
};

export default Step2Section;
